import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData, cloneData,
  cloneDataOrNull, cloneListOfCloneable, fromData, fromDataOrNull, isSameData, isSameList,
} from '../dto/dto.js';
import dayjs from 'dayjs';
import { LegacyCustomerOrderBase } from '../../contracts/customer-order.js';
import { TicketOrderStatus } from './ticket-order-status.js';
import { OnlineEventTicketOrderItem } from './online-event-ticket-order-item.js';
import { MapUtils } from '../../utility/map-utils.js';
import {
  fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString,
  toEnumFromStringOrNull,
} from '../../serialization/enum-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { EventTicket, EventTicketStatus } from './event-ticket.js';
import { OnlinePaymentDetails } from './online-payment-details.js';
import { PaymentType } from './payment-type.js';
import { EventTicketOrder } from './event-ticket-order.js';
import { VersionRange } from '../core/version-range.js';
import { PaymentStatus } from './payment-status.js';
import { LegacyEventPromoCode } from './discounts/legacy-event-promo-code.js';
import { Discount } from './discounts/discount.js';
import { Price } from './price.js';
import { isNotNullOrUndefined } from '../../utility/null-safety.js';
import { Language } from './language.js';
import { PaymentMethod, getDefaultPaymentMethodForPaymentType } from './payment-method.js';
import { CustomerProfile } from './customer-profile.js';
import { CustomerAccountBase } from './customer-account-base.js';
import { DiscountFactory } from '../../factories/discount-factory.js';
import { LegacyOrderHook } from './legacy-order-hook.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { Refund } from './discounts/refund.js';
import { SerializationException } from '../../exceptions/serialization-exception.js';
import { InvoiceVat } from './invoicing/invoice-vat.js';
import { TimeZoneRegion } from './time-zone-region.js';
import { ContactDetails } from './contact-details.js';
import { EntityMixin } from '../../components/core/entity-mixin.js';
import { JSONUtils } from '../../utility/json-utils.js';
import { OrderSideEffect } from './orders/order-side-effect.js';
import { ComponentList } from '../../components/core/component-list.js';
import { OrderSideEffectType } from './orders/order-side-effect-type.js';
import { ProductOrderItem } from './product-order-item.js';
import { EventBookingStrategy } from './event-booking-protocol/event-booking-strategy.js';
import { Country } from './country.js';
import { OrderDomain } from './order-domain.js';
import { OrderSource } from './order-source.js';

export class OnlineEventTicketOrder extends EntityMixin(DataModel) implements LegacyCustomerOrderBase {
  static readonly TYPE_ID: string = 'f0b0d5cdc7a44b6e839f53a9163e8e87';
  static readonly SCHEMA_VERSION: number = 20;

  public readonly isLegacyOrder = true;
  public readonly source = OrderSource.Client;

  public readonly userId: string | null;
  public readonly customerId: string | null;
  public readonly language: Language | null;
  public readonly country: Country;
  public readonly timezone: TimeZoneRegion;
  public readonly eventId: string;

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly status: TicketOrderStatus,
    public readonly domain: OrderDomain,
    public readonly paymentType: PaymentType,
    public readonly paymentMethod: PaymentMethod,
    public readonly onlinePaymentDetails: OnlinePaymentDetails | null,
    public readonly isGuestOrder: boolean,
    public readonly cancelledDate: dayjs.Dayjs | null,
    public readonly cancelledBy: string | null,
    public readonly sideEffects: OrderSideEffect[],
    public readonly businessRef: BusinessModuleRef,
    public readonly tickets: EventTicket[],
    public readonly products: ProductOrderItem[],
    public readonly refunds: Refund[],
    public readonly components: ComponentList,
    public readonly donation: Price | null,
    public discount: Discount | null,
    public standardApplicationFee: Price | null,
    public standardApplicationFeeVat: InvoiceVat | null,
    public productApplicationFee: Price | null,
    public productApplicationFeeVat: InvoiceVat | null,
    public readonly bookingStrategy: EventBookingStrategy,
    public readonly customer: CustomerProfile | null,
    public readonly contactDetails: ContactDetails,
  ) {
    super();

    this.eventId = tickets[0].eventId;
    this.userId = tickets[0].userId;
    this.customerId = tickets[0].customerId;
    this.language = tickets[0].language;
    this.country = tickets[0].country;
    this.timezone = tickets[0].timezone;
  }

  get $typeId(): string {
    return OnlineEventTicketOrder.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(18, OnlineEventTicketOrder.SCHEMA_VERSION);
  }

  get isOpen(): boolean {
    return this.status === TicketOrderStatus.Open;
  }

  get isCanceled(): boolean {
    return this.status === TicketOrderStatus.Canceled;
  }

  static create(args: {
    uniqueId: string,
    creatorId: string,
    creationDate: dayjs.Dayjs,
    status: TicketOrderStatus,
    domain: OrderDomain,
    paymentType: PaymentType,
    paymentMethod: PaymentMethod,
    onlinePaymentDetails: OnlinePaymentDetails | null,
    donation: Price | null,
    standardApplicationFee: Price | null,
    standardApplicationFeeVat: InvoiceVat | null,
    productApplicationFee: Price | null,
    productApplicationFeeVat: InvoiceVat | null,
    isGuestOrder: boolean,
    businessRef: BusinessModuleRef,
    tickets: EventTicket[],
    products: ProductOrderItem[],
    refunds: Refund[],
    discount: Discount | null,
    components: ComponentList,
    bookingStrategy: EventBookingStrategy,
    customer: CustomerProfile | null,
    contactDetails: ContactDetails,
    sideEffects?: OrderSideEffect[],
    cancelledDate?: dayjs.Dayjs | null,
    cancelledBy?: string | null,
  }): OnlineEventTicketOrder {
    return new OnlineEventTicketOrder(
        args.uniqueId,
        args.creatorId,
        args.creationDate,
        args.status,
        args.domain,
        args.paymentType,
        args.paymentMethod,
        args.onlinePaymentDetails,
        args.isGuestOrder,
        args.cancelledDate ?? null,
        args.cancelledBy ?? null,
        args.sideEffects ?? [],
        args.businessRef,
        args.tickets,
        args.products,
        args.refunds,
        args.components,
        args.donation,
        args.discount,
        args.standardApplicationFee,
        args.standardApplicationFeeVat,
        args.productApplicationFee,
        args.productApplicationFeeVat,
        args.bookingStrategy,
        args.customer,
        args.contactDetails,
    );
  }

  static fromTicketOrder(order: EventTicketOrder): OnlineEventTicketOrder {
    return OnlineEventTicketOrder.create({
      uniqueId: order.uniqueId,
      creatorId: order.creatorId,
      creationDate: order.creationDate,
      status: order.status,
      domain: order.domain,
      paymentType: order.paymentType,
      paymentMethod: order.paymentMethod,
      onlinePaymentDetails: order.onlinePaymentDetails?.clone() ?? null,
      isGuestOrder: order.isGuestOrder,
      cancelledDate: TimeZone.cloneDateOrNull(order.cancelledDate),
      cancelledBy: order.cancelledBy,
      sideEffects: [], // do not copy over side effects from online order! the functionalities are independent
      businessRef: order.businessRef.clone(),
      tickets: cloneListOfCloneable(order.tickets),
      products: cloneListOfCloneable(order.products),
      refunds: cloneListOfCloneable(order.refunds),
      components: order.components.clone(),
      donation: order.donation?.clone() ?? null,
      discount: order.discount?.clone() ?? null,
      standardApplicationFee: order.standardApplicationFee.isSome ? order.standardApplicationFee.value.clone() : null,
      standardApplicationFeeVat: order.standardApplicationFeeVat.isSome ? order.standardApplicationFeeVat.value : null,
      productApplicationFee: order.productApplicationFee?.clone() ?? null,
      productApplicationFeeVat: order.productApplicationFeeVat,
      bookingStrategy: order.bookingStrategy,
      customer: order.customer,
      contactDetails: order.contactDetails,
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): OnlineEventTicketOrder {
    return new OnlineEventTicketOrder(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'creatorId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, '$status'), TicketOrderStatus),
        toEnumFromString(MapUtils.get<string>(map, 'domain'), OrderDomain),
        toEnumFromString(MapUtils.get<string>(map, 'paymentType'), PaymentType),
        toEnumFromString(MapUtils.get<string>(map, 'paymentMethod'), PaymentMethod),
        context.fromDataOrNull<OnlinePaymentDetails>(MapUtils.getOrNull<SerializedData>(map, 'onlinePaymentDetails')),
        MapUtils.get<boolean>(map, 'isGuestOrder'),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'cancelledDate')),
        MapUtils.getOrNull<string>(map, 'cancelledBy'),
        MapUtils.getList<OrderSideEffect>(map, 'sideEffects', (value) => context.fromData<OrderSideEffect>(value as SerializedData)),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.getList<EventTicket>(map, 'tickets', (value) => {
          return context.fromData<EventTicket>(value as SerializedData);
        }),
        MapUtils.getList<ProductOrderItem>(map, 'products', (value) => {
          return context.fromData<ProductOrderItem>(value as SerializedData);
        }),
        MapUtils.getList<Refund>(map, 'refunds', (value) => {
          return context.fromData<Refund>(value as SerializedData);
        }),
        ComponentList.deserialize(context, MapUtils.getList<SerializedData>(
            map,
            'components',
            (data) => JSONUtils.decodeRecord(data)),
        ),
        Price.decodeOrNull(MapUtils.getOrNull<string>(map, 'donation')),
        context.fromDataOrNull<Discount>(MapUtils.getOrNull<SerializedData>(map, 'discount')),
        Price.decodeOrNull(MapUtils.getOrNull<string>(map, 'standardApplicationFee')),
        toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'standardApplicationFeeVat'), InvoiceVat),
        Price.decodeOrNull(MapUtils.getOrNull<string>(map, 'productApplicationFee')),
        toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'productApplicationFeeVat'), InvoiceVat),
        toEnumFromString(MapUtils.getString(map, 'bookingStrategy'), EventBookingStrategy),
        context.fromDataOrNull<CustomerProfile>(MapUtils.getRecordOrNull(map, 'customer')),
        context.fromData<ContactDetails>(MapUtils.getRecord(map, 'contactDetails')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    context.storage.set('avoidOptimizeForQuery', true);

    const map = {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'customerId': this.customerId,
      'userId': this.userId,
      'creationDate': context.encodeDate(this.creationDate),
      '$status': fromEnumAsString(this.status),
      'domain': fromEnumAsString(this.domain),
      'paymentType': fromEnumAsString(this.paymentType),
      'paymentMethod': fromEnumAsString(this.paymentMethod),
      'onlinePaymentDetails': context.toDataOrNull(this.onlinePaymentDetails),
      'isGuestOrder': this.isGuestOrder,
      'cancelledDate': context.encodeDateOrNull(this.cancelledDate),
      'cancelledBy': this.cancelledBy,
      'sideEffects': this.sideEffects.map((e) => context.toData(e)),
      'donation': this.donation?.encode() ?? null,
      'standardApplicationFee': this.standardApplicationFee?.encode() ?? null,
      'standardApplicationFeeVat': fromEnumAsStringOrNull(this.standardApplicationFeeVat),
      'productApplicationFee': this.productApplicationFee?.encode() ?? null,
      'productApplicationFeeVat': fromEnumAsStringOrNull(this.productApplicationFeeVat),
      'businessRef': this.businessRef.encode(),
      'tickets': this.tickets.map((e) => context.toData(e)),
      'products': this.products.map((e) => context.toData(e)),
      'discount': context.toDataOrNull(this.discount),
      'refunds': this.refunds.map((e) => context.toData(e)),
      'eventId': this.eventId,
      'components': this.components.serialize(context),
      'bookingStrategy': fromEnumAsString(this.bookingStrategy),
      'customer': context.toDataOrNull(this.customer),
      'contactDetails': context.toData(this.contactDetails),
    };

    context.storage.remove('avoidOptimizeForQuery');
    return map;
  }

  clone(): this {
    return new OnlineEventTicketOrder(
        this.uniqueId,
        this.creatorId,
        TimeZone.cloneDate(this.creationDate),
        this.status,
        this.domain,
        this.paymentType,
        this.paymentMethod,
        this.onlinePaymentDetails?.clone() ?? null,
        this.isGuestOrder,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
        this.sideEffects.map((e) => e.clone()),
        this.businessRef.clone(),
        cloneListOfCloneable(this.tickets),
        cloneListOfCloneable(this.products),
        cloneListOfCloneable(this.refunds),
        this.components.clone(),
        this.donation?.clone() ?? null,
        this.discount?.clone() ?? null,
        this.standardApplicationFee?.clone() ?? null,
        this.standardApplicationFeeVat,
        this.productApplicationFee?.clone() ?? null,
        this.productApplicationFeeVat,
        this.bookingStrategy,
        cloneDataOrNull(this.customer),
        cloneData(this.contactDetails),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.creatorId === this.creatorId &&
      other.creationDate === this.creationDate &&
      other.status === this.status &&
      other.domain === this.domain &&
      other.paymentType === this.paymentType &&
      other.paymentMethod === this.paymentMethod &&
      other.isGuestOrder === this.isGuestOrder &&
      other.cancelledDate === this.cancelledDate &&
      other.cancelledBy === this.cancelledBy &&
      isSameList(other.sideEffects, this.sideEffects, isSameData) &&
      isSameData(other.onlinePaymentDetails, this.onlinePaymentDetails) &&
      isSameData(other.donation, this.donation) &&
      isSameData(other.standardApplicationFee, this.standardApplicationFee) &&
      other.standardApplicationFeeVat == this.standardApplicationFeeVat &&
      isSameData(other.productApplicationFee, this.productApplicationFee) &&
      other.productApplicationFeeVat == this.productApplicationFeeVat &&
      other.businessRef === this.businessRef &&
      other.bookingStrategy === this.bookingStrategy &&
      isSameData(other.discount, this.discount) &&
      isSameList(other.tickets, this.tickets, isSameData) &&
      isSameList(other.products, this.products, isSameData) &&
      isSameList(other.refunds, this.refunds, isSameData) &&
      isSameData(other.customer, this.customer) &&
      isSameData(other.contactDetails, this.contactDetails) &&
      isSameData(other.components, this.components);
  }

  copyAsAborted(): this {
    return this.copyWith({
      status: TicketOrderStatus.Aborted,
      tickets: this.tickets.map((e) => e.copyAsAborted()),
    });
  }

  copyAsCanceled(cancelledBy: string | null, cancelledDate: dayjs.Dayjs, isRefund: boolean): this {
    return this.copyWith({
      status: isRefund ? TicketOrderStatus.Refunded : TicketOrderStatus.Canceled,
      cancelledDate: this.cancelledDate ?? cancelledDate,
      cancelledBy: this.cancelledBy ?? cancelledBy,
      tickets: this.tickets.map((ticket) => {
        const shouldRefund = isRefund || ticket.status === EventTicketStatus.Refunded;

        return ticket.copyAsCanceled(cancelledBy, cancelledDate, shouldRefund, { shouldCancelSupplement: true });
      }),
    });
  }

  copyWithPaymentDetails(paymentDetails: OnlinePaymentDetails): this {
    return this.copyWith({
      onlinePaymentDetails: paymentDetails,
    });
  }

  copyWithPaymentStatus(status: PaymentStatus): this {
    return this.copyWith({
      onlinePaymentDetails: this.onlinePaymentDetails?.copyWith({ status }),
    });
  }

  copyWithPaymentMethod(paymentMethod: PaymentMethod): this {
    return this.copyWith({
      paymentMethod: paymentMethod,
    });
  }

  copyWithContactDetails(contactDetails: ContactDetails): this {
    return this.copyWith({
      contactDetails: contactDetails,
    });
  }

  copyWithTicketCloner(cloner: (t: EventTicket) => EventTicket): this {
    return this.copyWith({ tickets: this.tickets.map(cloner) });
  }

  copyWithCreationDate(creationDate: dayjs.Dayjs): this {
    return this.copyWith({
      creationDate: creationDate,
      tickets: this.tickets.map((e) => e.copyWith({
        creationDate: creationDate,
      })),
    });
  }

  assignToCustomerAccount(account: CustomerAccountBase): this {
    return this.copyWith({
      creatorId: account.uniqueId,
      customer: this.customer?.assignToCustomerAccount(account),
      tickets: this.tickets.map((e) => e.assignToCustomerAccount(account)),
    });
  }

  copyWith({
    uniqueId = this.uniqueId,
    creatorId = this.creatorId,
    creationDate = TimeZone.cloneDate(this.creationDate),
    status = this.status,
    domain = this.domain,
    paymentType = this.paymentType,
    paymentMethod = this.paymentMethod,
    onlinePaymentDetails = cloneDataOrNull(this.onlinePaymentDetails),
    isGuestOrder = this.isGuestOrder,
    cancelledDate = TimeZone.cloneDateOrNull(this.cancelledDate),
    cancelledBy = this.cancelledBy,
    sideEffects = cloneListOfCloneable(this.sideEffects),
    businessRef = cloneData(this.businessRef),
    tickets = cloneListOfCloneable(this.tickets),
    products = cloneListOfCloneable(this.products),
    refunds = cloneListOfCloneable(this.refunds),
    discount = cloneDataOrNull(this.discount),
    donation = cloneDataOrNull(this.donation),
    standardApplicationFee = cloneDataOrNull(this.standardApplicationFee),
    standardApplicationFeeVat = this.standardApplicationFeeVat,
    productApplicationFee = cloneDataOrNull(this.productApplicationFee),
    productApplicationFeeVat = this.productApplicationFeeVat,
    components = this.components.clone(),
    bookingStrategy = this.bookingStrategy,
    customer = cloneDataOrNull(this.customer),
    contactDetails = cloneData(this.contactDetails),
  }: {
    uniqueId?: string,
    creatorId?: string,
    creationDate?: dayjs.Dayjs,
    status?: TicketOrderStatus,
    domain?: OrderDomain,
    paymentType?: PaymentType,
    paymentMethod?: PaymentMethod,
    onlinePaymentDetails?: OnlinePaymentDetails | null,
    isGuestOrder?: boolean,
    cancelledDate?: dayjs.Dayjs | null,
    cancelledBy?: string | null,
    sideEffects?: OrderSideEffect[],
    businessRef?: BusinessModuleRef,
    tickets?: EventTicket[],
    products?: ProductOrderItem[],
    refunds?: Refund[],
    discount?: Discount | null,
    donation?: Price | null,
    standardApplicationFee?: Price | null,
    standardApplicationFeeVat?: InvoiceVat | null,
    productApplicationFee?: Price | null,
    productApplicationFeeVat?: InvoiceVat | null,
    components?: ComponentList,
    bookingStrategy?: EventBookingStrategy,
    customer?: CustomerProfile | null,
    contactDetails?: ContactDetails,
  } = {}): this {
    return new OnlineEventTicketOrder(
        uniqueId,
        creatorId,
        creationDate,
        status,
        domain,
        paymentType,
        paymentMethod,
        onlinePaymentDetails,
        isGuestOrder,
        cancelledDate,
        cancelledBy,
        sideEffects,
        businessRef,
        tickets,
        products,
        refunds,
        components,
        donation,
        discount,
        standardApplicationFee,
        standardApplicationFeeVat,
        productApplicationFee,
        productApplicationFeeVat,
        bookingStrategy,
        customer,
        contactDetails,
    ) as this;
  }

  getTotalFee(): Price | null {
    if(this.standardApplicationFee != null && this.productApplicationFee != null) {
      return this.standardApplicationFee.add(this.productApplicationFee);
    }
    else if(this.standardApplicationFee != null) {
      return this.standardApplicationFee.clone();
    }
    else if(this.productApplicationFee != null) {
      return this.productApplicationFee.clone();
    }

    return null;
  }

  tryGetCustomer(): CustomerProfile | null {
    return this.customer;
  }

  tryGetContactDetails(): ContactDetails {
    return this.contactDetails;
  }

  findSideEffect(effectType: OrderSideEffectType): OrderSideEffect | null {
    for(let i = 0; i < this.sideEffects.length; i++) {
      if(this.sideEffects[i].effectType === effectType) {
        return this.sideEffects[i];
      }
    }

    return null;
  }

  hasSideEffect(effectType: OrderSideEffectType): boolean {
    return this.findSideEffect(effectType) != null;
  }

  static encodedStatusSafe(status: TicketOrderStatus): string {
    return fromEnumAsString(status);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof OnlineEventTicketOrder;
  }

  static assertType(order: DataModel): order is OnlineEventTicketOrder {
    return order instanceof OnlineEventTicketOrder;
  }
}

export class OnlineEventTicketOrderBuilder extends DataModelBuilder<OnlineEventTicketOrder> {
  build(context: SerializationContext, map: SerializedData): OnlineEventTicketOrder {
    return OnlineEventTicketOrder.fromMap(context, map);
  }

  optimizeForQuery(context: SerializationContext, model: OnlineEventTicketOrder): SerializedData | null {
    let paymentStatus: PaymentStatus | null = null;

    if(model.paymentType === PaymentType.Online) {
      paymentStatus = model.onlinePaymentDetails?.status ?? null;
    }
    else {
      paymentStatus = PaymentStatus.Succeeded;
    }

    return {
      'paymentStatus': fromEnumAsStringOrNull(paymentStatus),
      'paymentType': fromEnumAsString(model.paymentType),
      'paymentMethod': fromEnumAsString(model.paymentMethod),
      'containsEventProducts': model.bookingStrategy === EventBookingStrategy.TicketsAndProducts && model.products.length > 0,
    };
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    MapUtils.replace(map, '$status', 'DELETED', fromEnumAsString(TicketOrderStatus.Canceled));
    this.migrateEnum(map, '$status', TicketOrderStatus, TicketOrderStatus.Open);
    this.migrateEnum(map, 'paymentType', PaymentType, PaymentType.OnDelivery);
    this.migrateEnum(map, 'paymentMethod', PaymentMethod, PaymentMethod.Cash);

    // Needed for deserializing invoices with a graceful creation date! See builder of [OnlinePaymentDetails].
    const creationDate = context.decodeDate(MapUtils.getInteger(map, 'creationDate'));
    context.storage.set('orderCreationDate', creationDate);

    if(versionRange.contains(OnlineEventTicketOrder.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OnlineEventTicketOrder.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    const legacyTicketData = version < 18 ?
      this.extractCustomerAndContactDetailsFromLegacyTicketData(version, map) : null;

    // In version 2 we added `usedPromoCodes` of type `EventPromoCode[]`.
    if(version < 2) version = 2;

    // In version 3 we replaced `usedPromoCodes` of type `LegacyEventPromoCode` with `discount` of type `Discount`.
    if(version < 3) {
      const usedPromoCodes = MapUtils.getList<LegacyEventPromoCode>(map, 'usedPromoCodes', (data) => context.fromData(data as SerializedData));
      const items = MapUtils.getList<OnlineEventTicketOrderItem>(map, 'items', (data) => context.fromData(data as SerializedData));
      const businessRef = items[0].businessRef;
      const discounts = usedPromoCodes.map<Discount>((code) => DiscountFactory.fromLegacyPromoCode(businessRef, code));

      if(discounts.length > 0) {
        for(const item of items) {
          for(let i = 0; i < item.tickets.length; i++) {
            item.tickets[i] = item.tickets[i].copyWith({
              discount: discounts[0],
            });
          }
        }

        map['items'] = items.map((item) => context.toData(item));
      }

      map['discount'] = null;
      version = 3;
    }

    // In version 4 we added `applicationFee` of type `Price | null`.
    if(version < 4) version = 4;

    // In version 5 we moved discount to order item.
    if(version < 5) {
      const discount = context.fromDataOrNull<Discount>(MapUtils.getOrNull<SerializedData>(map, 'discount'));
      if(isNotNullOrUndefined(discount)) {
        const items = MapUtils.getList<OnlineEventTicketOrderItem>(map, 'items', (data) => context.fromData(data as SerializedData));
        items[0].discount = discount;

        map['items'] = items.map((item) => context.toData(item));
      }

      version = 5;
    }

    // In this version parameter `paymentMethod` of type `PaymentMethod` was
    // introduced.
    if(version < 6) {
      version = 6;

      const paymentType: PaymentType = toEnumFromString(MapUtils.get<string>(map, 'paymentType'), PaymentType);
      map['paymentMethod'] = fromEnumAsString(getDefaultPaymentMethodForPaymentType(paymentType));
    }

    // In this version parameter `isGuestOrder` of type `boolean` was
    // introduced.
    if(version < 7) {
      map['isGuestOrder'] = false;
      version = 7;
    }

    // In this version the [cancelledDate] and [cancelledBy] fields were added
    if(version < 8) {
      version = 8;
    }

    // In version 9 we added `onCompleteHooks` of type `OrderHook[]`.
    if(version < 9) {
      map['onCompleteHooks'] = [];
      version = 9;
    }

    if(version < 10) {
      const items = MapUtils.getList<OnlineEventTicketOrderItem>(map, 'items', (data) => context.fromData(data as SerializedData));
      if(items.length != 1) {
        throw new SerializationException(`Order {${map['uniqueId']}} of {${map['creatorId']}} must have exactly one item.`);
      }

      context.storage.set('avoidOptimizeForQuery', true);

      map['businessRef'] = items[0].businessRef.encode();
      map['tickets'] = items[0].tickets.map((e) => context.toData(e));
      map['discount'] = context.toDataOrNull(items[0].discount);
      map['refunds'] = items[0].refunds.map((e) => context.toData(e));

      context.storage.remove('avoidOptimizeForQuery');
      version = 10;
    }

    // In this version parameter `applicationFeeVat` of type `InvoiceVat | null` was introduced.
    if(version < 11) {
      version = 11;
    }

    /**
     * Version 12:
     * - add `components` field of type {@link ComponentList}.
     */
    if(version < 12) {
      MapUtils.setIfNullOrUndefined(map, 'components', []);

      version = 12;
    }

    // In version 13 we added `donation` of type `Price | null`.
    if(version < 13) version = 13;

    if(version < 14) {
      const onCompleteHooks = MapUtils.getList<LegacyOrderHook>(map, 'onCompleteHooks', (value) => {
        return context.fromData<LegacyOrderHook>(JSONUtils.decodeRecord(value));
      });

      const sideEffects = onCompleteHooks.map((e) => e.toSideEffect()).filter(isNotNullOrUndefined);

      map['sideEffects'] = sideEffects.map((e) => context.toData(e));
      version = 14;
    }

    // In version 15
    // - we added `products` of type `ProductOrderItem[]`.
    // - we added `bookingStrategy` of type `EventBookingStrategy`.
    if(version < 15) {
      map['products'] = [];
      map['bookingStrategy'] = fromEnumAsString(EventBookingStrategy.Tickets);
      version = 15;
    }

    // In version 16
    // - we added `productApplicationFee` of type `Price | null`.
    // - we added `productApplicationFeeVat` of type `InvoiceVat | null`.
    if(version < 16) version = 16;

    // In version 17
    // - we renamed `applicationFee` to `standardApplicationFee`.
    // - we renamed `applicationFeeVat` to `standardApplicationFeeVat`.
    if(version < 17) {
      map['standardApplicationFee'] = map['applicationFee'];
      map['standardApplicationFeeVat'] = map['applicationFeeVat'];
      version = 17;
    }

    // In version 18
    // - we deprecated customer and contact details data from event ticket.
    // - we added standalone customer and contact details fields.
    if(version < 18) {
      map['customer'] = context.toDataOrNull(legacyTicketData!.customer);
      map['contactDetails'] = context.toData(legacyTicketData!.contactDetails);

      version = 18;
    }

    // In version 19
    // - we removed the `customer` and `contactDetails` fields from the ticket model
    if(version < 19) version = 19;

    // In version 20
    // - we added 'domain' of type 'OrderDomain'.
    if(version < 20) {
      map['domain'] = fromEnumAsString(OrderDomain.inTime);

      version = 20;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  private extractCustomerAndContactDetailsFromLegacyTicketData(schemaVersion: number, map: SerializedData): {
    customer: CustomerProfile | null,
    contactDetails: ContactDetails,
  } {
    // Customer is stored in the order starting with schema version {18}.
    if(schemaVersion >= 18) {
      throw new SerializationException(
          'Failed to extract customer from tickets of [OnlineEventTicketOrder]. Current schema version no longer stores the data in tickets.',
          { schemaVersion }
      );
    }

    let ticketData: SerializedData | null = null;

    // It's important that you do not do any serialization/deserialization here so as to not modify the ticket/item maps.
    // Modifying the maps will lead to migration corruption.
    if(schemaVersion < 10) {
      const items = MapUtils.getList<SerializedData>(map, 'items', (data) => data as SerializedData);
      if(items.length > 0) {
        const tickets = MapUtils.getList<SerializedData>(items[0], 'tickets', (value) => value as SerializedData);
        if(tickets.length > 0) {
          ticketData = tickets[0];
        }
      }
    }
    else {
      const tickets = MapUtils.getList<SerializedData>(map, 'tickets', (value) => value as SerializedData);
      if(tickets.length > 0) {
        ticketData = tickets[0];
      }
    }

    if(ticketData == null) {
      throw new SerializationException(
          'Failed to extract customer from tickets of [OnlineEventTicketOrder]. Order has no tickets.',
          { schemaVersion }
      );
    }

    const customerId = MapUtils.getStringOrNull(ticketData, 'customerId');
    const customerData = MapUtils.getRecordOrNull(ticketData, 'customer');
    const contactDetailsData = MapUtils.getRecordOrNull(ticketData, 'contactDetails');

    if(customerId != null && customerData == null) {
      throw new SerializationException(
          'Failed to extract customer from tickets of [OnlineEventTicketOrder]. Ticket and order schema versions are out of sync.',
          { orderSchema: schemaVersion, ticketSchema: ticketData['$schema_version'] ?? null }
      );
    }

    if(contactDetailsData == null) {
      throw new SerializationException(
          'Failed to extract contact details from tickets of [OnlineEventTicketOrder]. Ticket and order schema versions are out of sync.',
          { orderSchema: schemaVersion, ticketSchema: ticketData['$schema_version'] ?? null }
      );
    }

    return {
      customer: fromDataOrNull<CustomerProfile>(customerData),
      contactDetails: fromData<ContactDetails>(contactDetailsData),
    };
  }
}
